import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BookOpen,
  ChevronDown,
  FileText,
  Library,
  Plus,
} from "lucide-react";
import { isBookCollectionKey, knowledgeBaseLabel } from "@/lib/knowledgeBaseRegistry";
import {
  collectionKeysForLibrary,
  listKnowledgeLibraries,
  suggestLibraryId,
  type ResolvedKnowledgeLibrary,
} from "@/lib/knowledgeLibraryRegistry";
import { agentsHref } from "@/lib/atwAgentsHost";
import { libraryPath, OTHER_LIBRARY_ID } from "@/lib/knowledgeModel";
import {
  knowledgeArticlesPath,
  knowledgeBasePath,
  knowledgeBooksPath,
} from "@/lib/knowledgePaths";
import {
  KnowledgeChrome,
  KnowledgeError,
  KnowledgeNeedsBase,
  knowledgeCardPrimaryLinkClass,
  knowledgePrimaryLinkClass,
  knowledgeSecondaryLinkClass,
} from "@/pages/knowledge/KnowledgeChrome";
import { LibraryActionsMenu } from "@/pages/knowledge/LibraryActionsMenu";
import { useCustomBooks } from "@/pages/knowledge/useCustomBooks";
import { useCustomLibraries } from "@/pages/knowledge/useCustomLibraries";
import { useKnowledgeArticles } from "@/pages/knowledge/useKnowledgeArticles";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";

const fieldClass =
  "mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-white/20 dark:bg-zinc-900 dark:text-white";

function countArticles(keys: Set<string>, articles: { collectionKeys: string[] }[]): number {
  return articles.filter((article) =>
    article.collectionKeys.some((key) => isBookCollectionKey(key) && keys.has(key)),
  ).length;
}

export function LibrariesPage() {
  const navigate = useNavigate();
  const { books: customBooks } = useCustomBooks();
  const { createLibrary, updateLibrary, deleteLibrary, orgId, kbId } = useCustomLibraries();
  const { articles, loading, error } = useKnowledgeArticles();
  const { active, hasBases, activeId } = useKnowledgeBases();

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [idDraft, setIdDraft] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<ResolvedKnowledgeLibrary | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ResolvedKnowledgeLibrary | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!moreOpen) return;
    function onPointerDown(event: MouseEvent) {
      if (!moreRef.current?.contains(event.target as Node)) setMoreOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setMoreOpen(false);
    }
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  const bookKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const article of articles) {
      for (const key of article.collectionKeys) {
        if (!isBookCollectionKey(key)) continue;
        keys.add(key);
      }
    }
    for (const book of customBooks) keys.add(book.collectionKey);
    return keys;
  }, [articles, customBooks]);

  const libraryEntries = useMemo(
    () => listKnowledgeLibraries(orgId, kbId, customBooks),
    [customBooks, kbId, orgId],
  );

  const unassignedKeys = useMemo(
    () => collectionKeysForLibrary(OTHER_LIBRARY_ID, bookKeys, customBooks),
    [bookKeys, customBooks],
  );

  function openForm() {
    setShowForm(true);
    setFormError(null);
    setTitle("");
    setDescription("");
    setIdDraft("");
    setIdTouched(false);
  }

  function onTitleChange(value: string) {
    setTitle(value);
    if (!idTouched) setIdDraft(suggestLibraryId(value));
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    const result = await createLibrary({
      title,
      description: description.trim() || undefined,
      id: idDraft.trim() || undefined,
    });
    if (!result.ok) {
      setFormError(result.error);
      return;
    }
    setShowForm(false);
    void navigate(agentsHref(libraryPath(activeId, result.library.id)));
  }

  function openEdit(library: ResolvedKnowledgeLibrary) {
    setShowForm(false);
    setDeleteTarget(null);
    setEditTarget(library);
    setEditTitle(library.title);
    setEditDescription(library.description || "");
    setEditError(null);
  }

  function openDelete(library: ResolvedKnowledgeLibrary) {
    setShowForm(false);
    setEditTarget(null);
    setDeleteTarget(library);
    setDeleteError(null);
  }

  async function onEditSave(event: FormEvent) {
    event.preventDefault();
    if (!editTarget) return;
    setEditBusy(true);
    setEditError(null);
    const result = await updateLibrary(editTarget.id, {
      title: editTitle,
      description: editDescription.trim() || undefined,
    });
    setEditBusy(false);
    if (!result.ok) {
      setEditError(result.error);
      return;
    }
    setEditTarget(null);
  }

  async function onDeleteConfirm() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    const result = await deleteLibrary(deleteTarget.id);
    setDeleteBusy(false);
    if (!result.ok) {
      setDeleteError(result.error);
      return;
    }
    setDeleteTarget(null);
  }

  async function onDuplicate(library: ResolvedKnowledgeLibrary) {
    setFormError(null);
    const copyTitle = `${library.title} copy`;
    const result = await createLibrary({
      title: copyTitle,
      description: library.description || undefined,
      id: suggestLibraryId(copyTitle),
    });
    if (!result.ok) {
      setFormError(result.error);
      return;
    }
    void navigate(agentsHref(libraryPath(activeId, result.library.id)));
  }

  if (!hasBases) {
    return (
      <KnowledgeChrome
        title="Libraries"
        description="Optional groups of books inside a knowledge base. Create a knowledge base first."
        badge="Same articles"
        showBaseSwitcher={false}
      >
        <KnowledgeNeedsBase />
      </KnowledgeChrome>
    );
  }

  const showUnassignedCard = unassignedKeys.length > 0;
  const panelOpen = showForm || Boolean(editTarget) || Boolean(deleteTarget);
  const showEmpty =
    !loading && !panelOpen && libraryEntries.length === 0 && !showUnassignedCard;

  const moreMenu = (
    <div ref={moreRef} className="relative">
      <button
        type="button"
        onClick={() => setMoreOpen((value) => !value)}
        className={knowledgeSecondaryLinkClass}
        aria-label="More knowledge links"
        aria-haspopup="menu"
        aria-expanded={moreOpen}
      >
        More
        <ChevronDown className="h-4 w-4" aria-hidden />
      </button>
      {moreOpen ? (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 w-44 origin-top-right rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-white/10 dark:bg-zinc-900"
        >
          <Link
            role="menuitem"
            to={agentsHref(knowledgeBasePath(activeId))}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-zinc-100 dark:hover:bg-white/5"
            onClick={() => setMoreOpen(false)}
          >
            <Library className="h-4 w-4" aria-hidden />
            Knowledge base
          </Link>
          <Link
            role="menuitem"
            to={agentsHref(knowledgeBooksPath(activeId))}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-zinc-100 dark:hover:bg-white/5"
            onClick={() => setMoreOpen(false)}
          >
            <BookOpen className="h-4 w-4" aria-hidden />
            Books
          </Link>
          <Link
            role="menuitem"
            to={agentsHref(knowledgeArticlesPath(activeId))}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-zinc-100 dark:hover:bg-white/5"
            onClick={() => setMoreOpen(false)}
          >
            <FileText className="h-4 w-4" aria-hidden />
            Articles
          </Link>
          <Link
            role="menuitem"
            to={agentsHref(knowledgeBooksPath(activeId))}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-zinc-100 dark:hover:bg-white/5"
            onClick={() => setMoreOpen(false)}
          >
            <Plus className="h-4 w-4" aria-hidden />
            New book
          </Link>
        </div>
      ) : null}
    </div>
  );

  return (
    <KnowledgeChrome
      title="Libraries"
      description={`Optional groups of books inside "${active ? knowledgeBaseLabel(active) : "this knowledge base"}". Create a library, then add books inside it.`}
      badge="Same articles"
      actions={
        <>
          {moreMenu}
          <button type="button" onClick={openForm} className={knowledgePrimaryLinkClass}>
            <Plus className="h-4 w-4" aria-hidden />
            Library
          </button>
        </>
      }
    >
      {error ? <KnowledgeError message={error} /> : null}
      {formError && !showForm ? <KnowledgeError message={formError} /> : null}

      {showForm ? (
        <form
          onSubmit={onCreate}
          className="max-w-lg space-y-4 rounded-lg border border-slate-200 bg-white dark:border-white/10 dark:bg-zinc-950/40 p-5 shadow-sm"
        >
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">New library</h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-zinc-400">
              Creates a library in the org Knowledge registry under the active knowledge base. Add
              books from the library page.
            </p>
          </div>
          {formError ? <KnowledgeError message={formError} /> : null}
          <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
            Title
            <input
              type="text"
              value={title}
              onChange={(event) => onTitleChange(event.target.value)}
              required
              autoFocus
              placeholder="Customer help"
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
            Description <span className="font-normal text-slate-500 dark:text-zinc-500">(optional)</span>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={2}
              placeholder="Books and articles for external customers"
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
            Id <span className="font-normal text-slate-500 dark:text-zinc-500">(optional slug)</span>
            <input
              type="text"
              value={idDraft}
              onChange={(event) => {
                setIdTouched(true);
                setIdDraft(event.target.value);
              }}
              placeholder={suggestLibraryId(title || "library")}
              className={`${fieldClass} font-mono text-xs`}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={!title.trim()}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              Create library
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setFormError(null);
              }}
              className={knowledgeSecondaryLinkClass}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {editTarget ? (
        <form
          onSubmit={onEditSave}
          className="max-w-lg space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-zinc-950/40"
        >
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Edit library</h2>
            <p className="mt-1 font-mono text-xs text-slate-500 dark:text-zinc-500">{editTarget.id}</p>
          </div>
          {editError ? <KnowledgeError message={editError} /> : null}
          <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
            Title
            <input
              type="text"
              value={editTitle}
              onChange={(event) => setEditTitle(event.target.value)}
              required
              autoFocus
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
            Description <span className="font-normal text-slate-500 dark:text-zinc-500">(optional)</span>
            <textarea
              value={editDescription}
              onChange={(event) => setEditDescription(event.target.value)}
              rows={2}
              className={fieldClass}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={editBusy || !editTitle.trim()}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {editBusy ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditTarget(null);
                setEditError(null);
              }}
              className={knowledgeSecondaryLinkClass}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {deleteTarget ? (
        <div className="max-w-lg space-y-4 rounded-lg border border-red-300 bg-red-50 p-5 shadow-sm dark:border-red-400/40 dark:bg-zinc-950/40">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Delete library</h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-zinc-400">
              Delete <span className="font-medium text-slate-900 dark:text-white">{deleteTarget.title}</span>?
              Books and articles stay in the store; only this library grouping is removed.
            </p>
          </div>
          {deleteError ? <KnowledgeError message={deleteError} /> : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={deleteBusy}
              onClick={() => void onDeleteConfirm()}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
            >
              {deleteBusy ? "Deleting…" : "Delete library"}
            </button>
            <button
              type="button"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteError(null);
              }}
              className={knowledgeSecondaryLinkClass}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {showEmpty ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 dark:border-white/20 dark:bg-zinc-950/40 px-6 py-12 text-center">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">No libraries yet</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-slate-600 dark:text-zinc-400">
            Libraries group books inside this knowledge base. Create your first library, then add
            books from its page.
          </p>
          <button
            type="button"
            onClick={openForm}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
          >
            <Plus className="h-4 w-4" aria-hidden />
            Library
          </button>
        </div>
      ) : null}

      {!panelOpen && (libraryEntries.length > 0 || showUnassignedCard) ? (
        <div className="grid gap-4 md:grid-cols-2">
          {libraryEntries.map((library) => {
            const keysInLibrary = collectionKeysForLibrary(library.id, bookKeys, customBooks);
            const keySet = new Set(keysInLibrary);
            return (
              <article
                key={library.id}
                className="flex flex-col rounded-lg border border-slate-200 bg-white dark:border-white/10 dark:bg-zinc-950/40 p-5 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
                    <Library className="h-5 w-5 text-slate-500 dark:text-zinc-500" aria-hidden />
                    {library.title}
                  </h2>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-white/10 dark:text-zinc-300">
                    {loading ? "…" : `${countArticles(keySet, articles).toLocaleString()} articles`}
                  </span>
                </div>
                {library.description ? (
                  <p className="mt-2 text-sm text-slate-600 dark:text-zinc-400">{library.description}</p>
                ) : library.source === "inferred" ? (
                  <p className="mt-2 text-sm text-slate-600 dark:text-zinc-400">
                    Inferred from book assignments in this knowledge base. Open to review or save
                    the library.
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-slate-600 dark:text-zinc-400">
                    Books you add while this library is open are grouped here.
                  </p>
                )}
                <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500 dark:text-zinc-500">
                  <BookOpen className="h-3.5 w-3.5" aria-hidden />
                  {keysInLibrary.length.toLocaleString()}{" "}
                  {keysInLibrary.length === 1 ? "book" : "books"}
                </p>
                <p className="mt-1 font-mono text-[11px] text-slate-500 dark:text-zinc-500">{library.id}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Link
                    to={agentsHref(libraryPath(activeId, library.id))}
                    className={knowledgeCardPrimaryLinkClass.replace(/^mt-4\s+/, "")}
                  >
                    Open library
                  </Link>
                  {library.source === "registry" ? (
                    <LibraryActionsMenu
                      onDuplicate={() => void onDuplicate(library)}
                      onEdit={() => openEdit(library)}
                      onDelete={() => openDelete(library)}
                    />
                  ) : null}
                </div>
              </article>
            );
          })}

          {showUnassignedCard ? (
            <article className="flex flex-col rounded-lg border border-slate-200 bg-white dark:border-white/10 dark:bg-zinc-950/40 p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
                  <Library className="h-5 w-5 text-slate-500 dark:text-zinc-500" aria-hidden />
                  Books library
                </h2>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-white/10 dark:text-zinc-300">
                  {loading
                    ? "…"
                    : `${countArticles(new Set(unassignedKeys), articles).toLocaleString()} articles`}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-600 dark:text-zinc-400">
                Books not assigned to a named library, including keys from articles.
              </p>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500 dark:text-zinc-500">
                <BookOpen className="h-3.5 w-3.5" aria-hidden />
                {unassignedKeys.length.toLocaleString()}{" "}
                {unassignedKeys.length === 1 ? "book" : "books"}
              </p>
              <Link to={agentsHref(libraryPath(activeId, OTHER_LIBRARY_ID))} className={knowledgeCardPrimaryLinkClass}>
                Open books
              </Link>
            </article>
          ) : null}
        </div>
      ) : null}
    </KnowledgeChrome>
  );
}
